import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { ArrowLeft, Trash2, Loader2, Calendar, DollarSign, CheckCircle, TrendingUp, Clock, ChevronDown, AlertTriangle, ChevronRight } from "lucide-react";
import type { Deal, Contact, Account, Facility } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { DetailPageShell } from "@/components/layout/detail-page-shell";
import { InlineTextField } from "@/components/inline-edit/inline-text-field";
import { InlineSelectField } from "@/components/inline-edit/inline-select-field";
import { InlineDateField } from "@/components/inline-edit/inline-date-field";
import { InlineSwitchField } from "@/components/inline-edit/inline-switch-field";
import { InlineTextareaField } from "@/components/inline-edit/inline-textarea-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useTabState } from "@/hooks/use-tab-state";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { buildBreadcrumbsFromUrl } from "@/lib/breadcrumbs";
import { FacilitiesPanel } from "@/components/facilities-panel";
import { DealPartiesPanel } from "@/components/deal-parties-panel";
import { DealTasksPanel } from "@/components/deal-tasks-panel";
import { DealSecuritiesTab } from "@/components/deals/deal-securities-tab";
import { CashFlowProjectionsPanel } from "@/components/financial/panels/cash-flow-projections-panel";
import { ServiceabilityDashboardPanel } from "@/components/financial/panels/serviceability-dashboard-panel";
import { DealFinancialHub } from "@/components/financial/deal-analysis/deal-financial-hub";
import { DealDocumentsTab } from "@/components/documents/deal-documents-tab";
import { DealSubmissionsTab } from "@/components/deals/deal-submissions-tab";
import { ActivityFeed } from "@/components/activity/activity-feed";
import { DealCommentsPanel } from "@/components/panels/deal-comments-panel";
import { FileCompliancePanel } from "@/components/panels/file-compliance-panel";
import { DocumentSecurityPanel } from "@/components/panels/document-security-panel";
import { LenderMatchResultsPanel } from "@/components/financial/panels/lender-match-results-panel";
import { StageTransitionModal } from "@/components/modals/stage-transition-modal";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { GoldenPathStepper } from "@/components/golden-path/golden-path-stepper";

// CRITICAL: These options MUST match the dealTypeEnum in shared/schema.ts
// Database enum: ["equipment", "property", "working_capital", "residential", "business_acquisition", "smsf_finance", "development_finance", "other"]
const DEAL_TYPE_OPTIONS = [
  { value: "equipment", label: "Equipment Finance" },
  { value: "property", label: "Property Finance" },
  { value: "working_capital", label: "Working Capital" },
  { value: "residential", label: "Residential" },
  { value: "business_acquisition", label: "Business Acquisition" },
  { value: "smsf_finance", label: "SMSF Finance" },
  { value: "development_finance", label: "Development Finance" },
  { value: "other", label: "Other" },
];

// Q9 Transaction Lifecycle Options
const TRANSACTION_STAGE_OPTIONS = [
  { value: "scenario", label: "Scenario (Exploration)" },
  { value: "deal", label: "Deal (Application)" },
  { value: "funded_loan", label: "Funded Loan" },
];

const SCENARIO_SUBSTAGE_OPTIONS = [
  { value: "initial_inquiry", label: "Initial Inquiry" },
  { value: "gathering_info", label: "Gathering Info" },
  { value: "seeking_indicatives", label: "Seeking Indicatives" },
  { value: "preparing_proposal", label: "Preparing Proposal" },
  { value: "awaiting_decision", label: "Awaiting Decision" },
];

const DEAL_SUBSTAGE_OPTIONS = [
  { value: "application_prep", label: "Application Prep" },
  { value: "submitted", label: "Submitted" },
  { value: "lender_assessment", label: "Lender Assessment" },
  { value: "conditional_approval", label: "Conditional Approval" },
  { value: "formal_approval", label: "Formal Approval" },
  { value: "settlement_booked", label: "Settlement Booked" },
  { value: "settled", label: "Settled" },
];


const COMMISSION_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "invoiced", label: "Invoiced" },
  { value: "received", label: "Received" },
  { value: "written_off", label: "Written Off" },
];

const SOURCE_AT_INTAKE_OPTIONS = [
  { value: "not_recorded", label: "Not recorded" },
  { value: "website", label: "Website" },
  { value: "referral", label: "Referral" },
  { value: "cold_call", label: "Cold call" },
  { value: "email_campaign", label: "Email / newsletter" },
  { value: "social_media", label: "Social media" },
  { value: "event", label: "Event" },
  { value: "partner", label: "Partner / advisor" },
  { value: "existing_client", label: "Existing / past client" },
  { value: "other", label: "Other" },
];

const FINANCE_READINESS_STAGE_OPTIONS = [
  { value: "not_recorded", label: "Not recorded" },
  { value: "check_completed", label: "Readiness Check completed" },
  { value: "plan_delivered", label: "Readiness Plan delivered" },
  { value: "converted", label: "Progressed to application" },
  { value: "not_proceeding", label: "Not proceeding" },
];

const CONVERSION_EVENT_OPTIONS = [
  { value: "not_recorded", label: "Not recorded" },
  { value: "application_lodged", label: "Application lodged" },
  { value: "settled", label: "Settled" },
];

const TRANSACTION_STAGE_COLORS: Record<string, string> = {
  scenario: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  deal: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  funded_loan: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
};


function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Stage History Panel Component
interface StageHistoryEntry {
  id: string;
  fromTransactionStage?: string;
  toTransactionStage?: string;
  fromScenarioSubstage?: string;
  toScenarioSubstage?: string;
  fromDealSubstage?: string;
  toDealSubstage?: string;
  changedAt: string;
  changedBy?: string;
  reason?: string;
  systemGenerated: boolean;
}

function StageHistoryPanel({ dealId }: { dealId: string }) {
  const { data: history = [], isLoading } = useQuery<StageHistoryEntry[]>({
    queryKey: [`/api/crm/deals/${dealId}/stage-history`],
    enabled: !!dealId,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-2">
          <p className="text-sm text-muted-foreground text-center py-4">Loading stage history...</p>
        </CardContent>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card data-testid="stage-history-empty">
        <CardContent className="pt-2">
          <div className="text-center py-8">
            <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No stage transitions yet</p>
            <p className="text-xs text-muted-foreground mt-1">Stage changes will appear here as the deal progresses</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="stage-history-panel">
      <CardContent className="pt-2">
        <div className="space-y-2" data-testid="stage-history-list">
          {history.map((entry, index) => (
            <div key={entry.id} data-testid={`stage-history-entry-${index}`} className="relative pb-3 border-b last:border-0 last:pb-0">
              {index < history.length - 1 && (
                <div className="absolute left-2 top-8 bottom-0 w-0.5 bg-muted" />
              )}
              <div className="flex gap-3">
                <div className="relative">
                  <div className="h-4 w-4 rounded-full bg-primary ring-4 ring-background" />
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    {entry.toTransactionStage && (
                      <Badge
                        variant="secondary"
                        className={`text-xs ${
                          TRANSACTION_STAGE_COLORS[entry.toTransactionStage] || "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {TRANSACTION_STAGE_OPTIONS.find((s) => s.value === entry.toTransactionStage)?.label ||
                          entry.toTransactionStage}
                      </Badge>
                    )}
                    {entry.toScenarioSubstage && (
                      <Badge variant="outline" className="text-xs">
                        {SCENARIO_SUBSTAGE_OPTIONS.find((s) => s.value === entry.toScenarioSubstage)?.label ||
                          entry.toScenarioSubstage}
                      </Badge>
                    )}
                    {entry.toDealSubstage && (
                      <Badge variant="outline" className="text-xs">
                        {DEAL_SUBSTAGE_OPTIONS.find((s) => s.value === entry.toDealSubstage)?.label ||
                          entry.toDealSubstage}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(entry.changedAt)}
                    {entry.systemGenerated && " (automatic)"}
                  </p>
                  {entry.reason && <p className="text-xs text-muted-foreground italic">{entry.reason}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DealDetail() {
  const [, params] = useRoute("/deals/:id");
  const [, navigate] = useLocation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRevertDialogOpen, setIsRevertDialogOpen] = useState(false); // SPRINT-3 AC005
  // F007: Stage transition modal state
  const [isTransitionModalOpen, setIsTransitionModalOpen] = useState(false);
  const [selectedTransition, setSelectedTransition] = useState<{
    targetStage: string;
    targetSubstage: string;
    label: string;
    requiresReadinessCheck: boolean;
  } | null>(null);

  // Use URL hash for tab persistence
  const [activeTab, setActiveTab] = useTabState("overview");

  const { toast } = useToast();

  const { data: deal, isLoading } = useQuery<Deal>({
    queryKey: ["/api/crm/deals", params?.id],
    enabled: !!params?.id,
  });

  const { data: borrower } = useQuery<Contact | Account>({
    queryKey: deal?.borrowerType === "contact"
      ? ["/api/crm/contacts", deal?.borrowerId]
      : ["/api/crm/accounts", deal?.borrowerId],
    enabled: !!deal?.borrowerId && !!deal?.borrowerType,
  });

  // F007: Fetch valid transitions for action buttons
  const { data: transitionsData } = useQuery<{
    currentStage: string;
    currentSubstage: string;
    validTransitions: Array<{
      targetStage: string;
      targetSubstage: string;
      label: string;
      requiresReadinessCheck: boolean;
    }>;
  }>({
    queryKey: [`/api/crm/deals/${params?.id}/valid-transitions`],
    enabled: !!params?.id && !!deal,
  });

  // Filter out scenario→deal promotion from header — handled by sidebar CTA (#485)
  const validTransitions = (transitionsData?.validTransitions || []).filter(
    (t) => !(deal?.transactionStage === "scenario" && t.targetStage === "deal")
  );
  const primaryTransition = validTransitions.length > 0 ? validTransitions[0] : null;
  const additionalTransitions = validTransitions.slice(1);

  // F007 #457: Fetch facilities to calculate total loan amounts
  const { data: facilities = [] } = useQuery<Facility[]>({
    queryKey: ["/api/crm/deals", params?.id, "facilities"],
    enabled: !!params?.id && !!deal,
  });

  const totalRequestedAmount = facilities.reduce((sum, f) => {
    const val = f.requestedAmount ? parseFloat(f.requestedAmount) : 0;
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const totalApprovedAmount = facilities.reduce((sum, f) => {
    const val = f.approvedLimit ? parseFloat(f.approvedLimit) : 0;
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const handleTransitionClick = (transition: {
    targetStage: string;
    targetSubstage: string;
    label: string;
    requiresReadinessCheck: boolean;
  }) => {
    setSelectedTransition(transition);
    setIsTransitionModalOpen(true);
  };

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Deal>) => {
      const response = await apiRequest("PATCH", `/api/crm/deals/${params?.id}`, updates);
      return response;
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ["/api/crm/deals", params?.id] });
      const previousDeal = queryClient.getQueryData(["/api/crm/deals", params?.id]);
      if (previousDeal) {
        queryClient.setQueryData(["/api/crm/deals", params?.id], {
          ...previousDeal,
          ...updates,
        });
      }
      return { previousDeal };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousDeal) {
        queryClient.setQueryData(["/api/crm/deals", params?.id], context.previousDeal);
      }
      toast({
        title: "Error",
        description: error.message || "Failed to save changes",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", params?.id] });
    },
  });

  const handleFieldUpdate = async (field: keyof Deal, value: any): Promise<void> => {
    await updateMutation.mutateAsync({ [field]: value });
  };

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/crm/deals/${params?.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals"] });
      toast({
        title: "Deal deleted",
        description: "The deal has been successfully deleted.",
      });
      navigate("/deals");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete deal",
        variant: "destructive",
      });
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
    setIsDeleteDialogOpen(false);
  };

  // SPRINT-3: Revert to scenario mutation
  const revertMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/crm/deals/${params?.id}/revert-to-scenario`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", params?.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", params?.id, "facilities"] });
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${params?.id}/parties`] });
      toast({
        title: "Deal reverted to scenario",
        description: "The deal has been successfully reverted to scenario stage.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to revert deal",
        variant: "destructive",
      });
    },
  });

  const handleNavItemClick = (href: string) => {
    const tabValue = href.replace("#", "");
    setActiveTab(tabValue);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading deal...</p>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-3 text-sm">The deal you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/deals")} size="sm">Back to Deals</Button>
        </div>
      </div>
    );
  }

  const searchParams = new URLSearchParams(window.location.search);
  const breadcrumbs = buildBreadcrumbsFromUrl(
    searchParams,
    [
      { label: "Home", href: "/" },
      { label: "Deals", href: "/deals" },
      { label: deal.dealName },
    ]
  );

  const getBorrowerName = () => {
    if (!borrower) return "-";
    if (deal.borrowerType === "contact") {
      const contact = borrower as Contact;
      return [contact.firstName, contact.lastName].filter(Boolean).join(" ") || "-";
    } else {
      const account = borrower as Account;
      return account.legalName || "-";
    }
  };

  return (
    <>
      <DetailPageShell
        title={deal.dealName}
        breadcrumbs={breadcrumbs}
        value={activeTab}
        onValueChange={setActiveTab}
        onNavItemClick={handleNavItemClick}
        badge={
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className={`capitalize text-xs ${
                  TRANSACTION_STAGE_COLORS[deal.transactionStage || "scenario"] || "bg-gray-100 text-gray-800"
                }`}
              >
                {TRANSACTION_STAGE_OPTIONS.find((s) => s.value === deal.transactionStage)?.label ||
                  deal.transactionStage ||
                  "Scenario"}
              </Badge>
              {deal.transactionStage === "scenario" && deal.scenarioSubstage && (
                <Badge variant="outline" className="text-xs">
                  {SCENARIO_SUBSTAGE_OPTIONS.find((s) => s.value === deal.scenarioSubstage)?.label ||
                    deal.scenarioSubstage}
                </Badge>
              )}
              {deal.transactionStage === "deal" && deal.dealSubstage && (
                <Badge variant="outline" className="text-xs">
                  {DEAL_SUBSTAGE_OPTIONS.find((s) => s.value === deal.dealSubstage)?.label || deal.dealSubstage}
                </Badge>
              )}
            </div>

          </div>
        }
        navItems={[
          { label: "Overview", href: "#overview" },
          { label: "Facilities", href: "#facilities" },
          { label: "Securities", href: "#securities" },
          { label: "Financials", href: "#financials" },
          { label: "Lender Matches", href: "#lender-matches" },
          { label: "Parties", href: "#parties" },
          { label: "Stage History", href: "#stage-history" },
          { label: "Documents", href: "#documents" },
          { label: "Tasks", href: "#tasks" },
          { label: "Submissions", href: "#submissions" },
          { label: "Compliance", href: "#compliance" },
          { label: "Communications", href: "#communications" },
          { label: "Activity", href: "#activity" },
        ]}
        actions={
          <div className="flex gap-2">
            {/* F007: Primary transition button */}
            {primaryTransition && (
              <Button
                variant="default"
                size="sm"
                onClick={() => handleTransitionClick(primaryTransition)}
                data-testid="button-primary-transition"
              >
                <ChevronRight className="mr-1 h-4 w-4" />
                {primaryTransition.label}
              </Button>
            )}

            {/* F007: Additional transitions dropdown */}
            {additionalTransitions.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="button-more-transitions">
                    More Actions
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {additionalTransitions.map((t) => (
                    <DropdownMenuItem
                      key={`${t.targetStage}-${t.targetSubstage}`}
                      onClick={() => handleTransitionClick(t)}
                    >
                      {t.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* SPRINT-3: Revert to Scenario button (only for deals) */}
            {deal.transactionStage === "deal" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsRevertDialogOpen(true)}
                disabled={revertMutation.isPending}
                data-testid="button-revert"
              >
                {revertMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowLeft className="mr-2 h-4 w-4" />
                )}
                Revert to Scenario
              </Button>
            )}

            <Button variant="outline" size="sm" onClick={() => {
              if (sessionStorage.getItem('cameFromApp') === 'true') {
                window.history.back();
              } else {
                navigate("/deals");
              }
            }} data-testid="button-back">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>
        }
        extraContent={
          <GoldenPathStepper
            dealId={deal.id}
            onNavigateTab={setActiveTab}
          />
        }
        tabs={[
          {
            value: "overview",
            label: "Overview",
            content: (
              <div id="overview" className="space-y-2">
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">TRANSACTION STAGE</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineSelectField
                        value={deal.transactionStage || "scenario"}
                        options={TRANSACTION_STAGE_OPTIONS}
                        onSave={(value) => handleFieldUpdate("transactionStage", value)}
                        label="Transaction Stage"
                        placeholder="Select stage"
                        testId="select-transaction-stage"
                      />
                      {deal.transactionStage === "scenario" && (
                        <InlineSelectField
                          value={deal.scenarioSubstage || ""}
                          options={SCENARIO_SUBSTAGE_OPTIONS}
                          onSave={(value) => handleFieldUpdate("scenarioSubstage", value)}
                          label="Scenario Substage"
                          placeholder="Select substage"
                          testId="select-scenario-substage"
                        />
                      )}
                      {deal.transactionStage === "deal" && (
                        <InlineSelectField
                          value={deal.dealSubstage || ""}
                          options={DEAL_SUBSTAGE_OPTIONS}
                          onSave={(value) => handleFieldUpdate("dealSubstage", value)}
                          label="Deal Substage"
                          placeholder="Select substage"
                          testId="select-deal-substage"
                        />
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">DEAL DETAILS</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        value={deal.dealName}
                        onSave={(value) => handleFieldUpdate("dealName", value)}
                        label="Deal Name"
                        placeholder="Enter deal name"
                        testId="input-deal-name"
                      />
                      <InlineSelectField
                        value={deal.dealType}
                        options={DEAL_TYPE_OPTIONS}
                        onSave={(value) => handleFieldUpdate("dealType", value)}
                        label="Deal Type"
                        placeholder="Select type"
                        testId="select-deal-type"
                      />
                      <InlineTextField
                        value={deal.lenderName}
                        onSave={(value) => handleFieldUpdate("lenderName", value || null)}
                        label="Lender Name"
                        placeholder="Optional"
                        testId="input-lender-name"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">KEY DATES</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineDateField
                        value={deal.expectedSettlementDate}
                        onSave={(value) => handleFieldUpdate("expectedSettlementDate", value)}
                        label="Expected Settlement"
                        placeholder="Pick a date"
                        testId="input-expected-settlement"
                      />
                      <InlineDateField
                        value={deal.actualSettlementDate}
                        onSave={(value) => handleFieldUpdate("actualSettlementDate", value)}
                        label="Actual Settlement"
                        placeholder="Pick a date"
                        testId="input-actual-settlement"
                      />
                      <InlineDateField
                        value={deal.financeDate}
                        onSave={(value) => handleFieldUpdate("financeDate", value)}
                        label="Finance Date"
                        placeholder="Pick a date"
                        testId="input-finance-date"
                      />
                      <InlineDateField
                        value={deal.lodgedDate}
                        onSave={(value) => handleFieldUpdate("lodgedDate", value)}
                        label="Lodged Date"
                        placeholder="Pick a date"
                        testId="input-lodged-date"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="deal-operational-measurement-card">
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">OPERATIONAL MEASUREMENT</h3>
                    <p className="text-xs text-muted-foreground mb-2">
                      Finance readiness and conversion tracking for this deal.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineSelectField
                        value={deal.sourceAtIntake || "not_recorded"}
                        options={SOURCE_AT_INTAKE_OPTIONS}
                        onSave={(value) => handleFieldUpdate(
                          "sourceAtIntake",
                          value === "not_recorded" ? null : value,
                        )}
                        label="Source at intake"
                        placeholder="Not recorded"
                        testId="select-source-at-intake"
                      />
                      <InlineSelectField
                        value={deal.financeReadinessStage || "not_recorded"}
                        options={FINANCE_READINESS_STAGE_OPTIONS}
                        onSave={(value) => handleFieldUpdate(
                          "financeReadinessStage",
                          value === "not_recorded" ? null : value,
                        )}
                        label="Finance readiness stage"
                        placeholder="Not recorded"
                        testId="select-finance-readiness-stage"
                      />
                      <InlineDateField
                        value={deal.planDeliveredDate}
                        onSave={(value) => handleFieldUpdate("planDeliveredDate", value)}
                        label="Finance plan delivered date"
                        placeholder="Not recorded"
                        testId="input-plan-delivered-date"
                      />
                      <InlineSwitchField
                        value={deal.bidSignoff}
                        onSave={(value) => handleFieldUpdate("bidSignoff", value)}
                        label="Licensed-human BID sign-off"
                        testId="switch-bid-signoff"
                      />
                      <InlineTextField
                        value={deal.licenseeInitials || ""}
                        onSave={(value) => handleFieldUpdate("licenseeInitials", value || null)}
                        label="Licensee initials"
                        placeholder="Required for sign-off"
                        testId="input-licensee-initials"
                      />
                      <InlineDateField
                        value={deal.bidSignedDate}
                        onSave={(value) => handleFieldUpdate("bidSignedDate", value)}
                        label="BID signed date"
                        placeholder="Required for sign-off"
                        testId="input-bid-signed-date"
                      />
                      <InlineSelectField
                        value={deal.conversionEvent || "not_recorded"}
                        options={CONVERSION_EVENT_OPTIONS}
                        onSave={(value) => handleFieldUpdate(
                          "conversionEvent",
                          value === "not_recorded" ? null : value,
                        )}
                        label="Conversion event"
                        placeholder="Not recorded"
                        testId="select-conversion-event"
                      />
                      <InlineDateField
                        value={deal.conversionEventDate}
                        onSave={(value) => handleFieldUpdate("conversionEventDate", value)}
                        label="Conversion event date"
                        placeholder="Event date"
                        testId="input-conversion-event-date"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-2">
                    <InlineTextareaField
                      value={deal.notes}
                      onSave={(value) => handleFieldUpdate("notes", value || null)}
                      label="Notes"
                      placeholder="Enter notes (optional)"
                      rows={4}
                      testId="input-notes"
                    />
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            value: "facilities",
            label: "Facilities",
            content: (
              <div id="facilities" className="space-y-2">
                <FacilitiesPanel dealId={deal.id} clientGroupId={deal.clientGroupId ?? undefined} />
              </div>
            ),
          },
          {
            value: "securities",
            label: "Securities",
            content: (
              <div id="securities" className="space-y-2">
                <DealSecuritiesTab dealId={deal.id} />
              </div>
            ),
          },
          {
            value: "financials",
            label: "Financials",
            content: (
              <div id="financials" className="space-y-2">
                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">LOAN AMOUNTS</h3>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Total Amount Requested</p>
                        <p className="text-sm font-medium">{formatCurrency(deal.amountRequested)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Total Amount Approved</p>
                        <p className="text-sm font-medium">{formatCurrency(deal.amountApproved)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Total Amount Settled</p>
                        <p className="text-sm font-medium text-green-600">{formatCurrency(deal.amountSettled)}</p>
                      </div>
                    </div>
                    {facilities.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <h4 className="text-xs font-semibold text-muted-foreground mb-1.5">FACILITY TOTALS ({facilities.length} {facilities.length === 1 ? "facility" : "facilities"})</h4>
                        <div className="grid grid-cols-2 gap-2">
                          <div data-testid="facility-total-requested">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Total Facility Requested</p>
                            <p className="text-sm font-medium">{totalRequestedAmount > 0 ? formatCurrency(totalRequestedAmount.toFixed(2)) : "-"}</p>
                          </div>
                          <div data-testid="facility-total-approved">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Total Facility Approved</p>
                            <p className="text-sm font-medium">{totalApprovedAmount > 0 ? formatCurrency(totalApprovedAmount.toFixed(2)) : "-"}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-2">
                    <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">COMMISSION</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <InlineTextField
                        value={deal.estimatedCommission || ""}
                        onSave={(value) => handleFieldUpdate("estimatedCommission", value || null)}
                        label="Estimated Commission"
                        placeholder="Enter amount"
                        testId="input-estimated-commission"
                      />
                      <InlineTextField
                        value={deal.actualCommission || ""}
                        onSave={(value) => handleFieldUpdate("actualCommission", value || null)}
                        label="Actual Commission"
                        placeholder="Enter amount"
                        testId="input-actual-commission"
                      />
                      <InlineSelectField
                        value={deal.commissionStatus || ""}
                        options={COMMISSION_STATUS_OPTIONS}
                        onSave={(value) => handleFieldUpdate("commissionStatus", value || null)}
                        label="Commission Status"
                        placeholder="Select status"
                        testId="select-commission-status"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Sprint 15: Deal Financial Analysis Hub */}
                <DealFinancialHub dealId={deal.id} />

                {/* F012: Serviceability Dashboard - Unified dashboard integrating all panels */}
                <ServiceabilityDashboardPanel dealId={deal.id} />

                {/* F011: Cash Flow Projections Panel */}
                <CashFlowProjectionsPanel dealId={deal.id} />
              </div>
            ),
          },
          {
            value: "lender-matches",
            label: "Lender Matches",
            content: (
              <div id="lender-matches" className="space-y-2" data-testid="tab-lender-matches-content">
                <LenderMatchResultsPanel dealId={deal.id} />
              </div>
            ),
          },
          {
            value: "parties",
            label: "Parties",
            content: (
              <div id="parties" className="space-y-2">
                <DealPartiesPanel
                  dealId={deal.id}
                  clientGroupId={deal.clientGroupId ?? undefined}
                  transactionStage={deal.transactionStage}
                />
              </div>
            ),
          },
          {
            value: "stage-history",
            label: "Stage History",
            content: (
              <div id="stage-history" className="space-y-2">
                <StageHistoryPanel dealId={deal.id} />
              </div>
            ),
          },
          {
            value: "documents",
            label: "Documents",
            content: (
              <div id="documents">
                <DealDocumentsTab dealId={deal.id} />
              </div>
            ),
          },
          {
            value: "tasks",
            label: "Tasks",
            content: (
              <div id="tasks">
                <DealTasksPanel dealId={deal.id} />
              </div>
            ),
          },
          {
            value: "submissions",
            label: "Submissions",
            content: (
              <div id="submissions" className="space-y-2">
                <DealSubmissionsTab dealId={deal.id} />
              </div>
            ),
          },
          {
            value: "compliance",
            label: "Compliance",
            content: (
              <div id="compliance" className="space-y-2" data-testid="tab-compliance-content">
                <FileCompliancePanel dealId={deal.id} />
                <DocumentSecurityPanel dealId={deal.id} />
              </div>
            ),
          },
          {
            value: "communications",
            label: "Communications",
            content: (
              <div id="communications" className="space-y-2">
                <ActivityFeed
                  entityType="deal"
                  entityId={deal.id}
                  lens="email"
                  testId="panel-deal-communications"
                />
              </div>
            ),
          },
          {
            value: "activity",
            label: "Activity",
            content: (
              <div id="activity" className="space-y-2">
                <ActivityFeed
                  entityType="deal"
                  entityId={deal.id}
                  testId="panel-deal-activity-feed"
                />
                <DealCommentsPanel dealId={deal.id} />
              </div>
            ),
          },
        ]}
        rightPanel={
          <div className="space-y-4" data-testid="deal-sidebar">
            {/* Transaction Lifecycle Visualization */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">TRANSACTION LIFECYCLE</h3>
              <div className="space-y-2">
                {/* Current Transaction Stage */}
                <div className="flex items-center gap-2">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      deal.transactionStage === "scenario"
                        ? "bg-blue-500 ring-2 ring-blue-500/30"
                        : "bg-blue-500"
                    }`}
                  />
                  <span className={`text-xs ${deal.transactionStage === "scenario" ? "font-semibold" : ""}`}>
                    Scenario
                  </span>
                </div>

                {/* Scenario Substages (if in scenario stage) */}
                {deal.transactionStage === "scenario" && (
                  <div className="ml-4 space-y-1 border-l-2 border-blue-200 pl-3">
                    {SCENARIO_SUBSTAGE_OPTIONS.map((substage) => (
                      <div
                        key={substage.value}
                        className={`flex items-center gap-2 text-xs ${
                          deal.scenarioSubstage === substage.value ? "font-semibold text-primary" : "text-muted-foreground"
                        }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${
                            deal.scenarioSubstage === substage.value
                              ? "bg-primary ring-2 ring-primary/30"
                              : "bg-muted"
                          }`}
                        />
                        {substage.label}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      deal.transactionStage === "deal"
                        ? "bg-purple-500 ring-2 ring-purple-500/30"
                        : deal.transactionStage === "funded_loan" || deal.transactionStage === "scenario"
                        ? "bg-purple-200"
                        : "bg-muted"
                    }`}
                  />
                  <span className={`text-xs ${deal.transactionStage === "deal" ? "font-semibold" : ""}`}>Deal</span>
                </div>

                {/* Deal Substages (if in deal stage) */}
                {deal.transactionStage === "deal" && (
                  <div className="ml-4 space-y-1 border-l-2 border-purple-200 pl-3">
                    {DEAL_SUBSTAGE_OPTIONS.map((substage) => (
                      <div
                        key={substage.value}
                        className={`flex items-center gap-2 text-xs ${
                          deal.dealSubstage === substage.value ? "font-semibold text-primary" : "text-muted-foreground"
                        }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${
                            deal.dealSubstage === substage.value ? "bg-primary ring-2 ring-primary/30" : "bg-muted"
                          }`}
                        />
                        {substage.label}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      deal.transactionStage === "funded_loan"
                        ? "bg-emerald-500 ring-2 ring-emerald-500/30"
                        : "bg-muted"
                    }`}
                  />
                  <span className={`text-xs ${deal.transactionStage === "funded_loan" ? "font-semibold" : ""}`}>
                    Funded Loan
                  </span>
                </div>
              </div>

              {/* Transition Actions */}
              {deal.transactionStage === "scenario" && deal.scenarioSubstage === "awaiting_decision" && (
                <div className="mt-3 pt-3 border-t">
                  <Button
                    size="sm"
                    className="w-full justify-start text-xs"
                    variant="default"
                    onClick={async () => {
                      await apiRequest("POST", `/api/crm/deals/${deal.id}/accept-proposal`);
                      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", deal.id] });
                    }}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Accept Proposal → Start Deal
                  </Button>
                </div>
              )}
              {deal.transactionStage === "deal" && deal.dealSubstage === "settled" && (
                <div className="mt-3 pt-3 border-t">
                  <Button
                    size="sm"
                    className="w-full justify-start text-xs"
                    variant="default"
                    onClick={async () => {
                      await apiRequest("POST", `/api/crm/deals/${deal.id}/mark-funded`);
                      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", deal.id] });
                    }}
                  >
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Mark as Funded Loan
                  </Button>
                </div>
              )}
            </div>

            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">BORROWER</h3>
              <div className="text-xs">
                <p className="text-muted-foreground capitalize">{deal.borrowerType}</p>
                <button
                  className="font-medium text-primary hover:underline text-left"
                  onClick={() => navigate(deal.borrowerType === "contact" ? `/contacts/${deal.borrowerId}` : `/accounts/${deal.borrowerId}`)}
                  data-testid="link-borrower"
                >
                  {getBorrowerName()}
                </button>
              </div>
            </div>

            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">KEY DATES</h3>
              <div className="space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <Calendar className="h-3 w-3 mt-0.5 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Expected Settlement</p>
                    <p className="font-medium">{formatDate(deal.expectedSettlementDate)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="h-3 w-3 mt-0.5 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Actual Settlement</p>
                    <p className="font-medium">{formatDate(deal.actualSettlementDate)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2" data-testid="sidebar-finance-date">
                  <Calendar className="h-3 w-3 mt-0.5 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Finance Date</p>
                    <p className="font-medium">{formatDate(deal.financeDate)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-3">
              <h3 className="text-xs font-semibold text-muted-foreground mb-1.5">COMMISSION SUMMARY</h3>
              <div className="space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <DollarSign className="h-3 w-3 mt-0.5 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Estimated</p>
                    <p className="font-medium">{formatCurrency(deal.estimatedCommission)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <DollarSign className="h-3 w-3 mt-0.5 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Actual</p>
                    <p className="font-medium">{formatCurrency(deal.actualCommission)}</p>
                  </div>
                </div>
                {deal.commissionStatus && (
                  <div className="mt-2">
                    <Badge variant="secondary" className="text-xs capitalize">
                      {deal.commissionStatus}
                    </Badge>
                  </div>
                )}
              </div>
            </div>

            {/* Danger Zone - RULE-SAFETY-001: Hide delete behind collapsible */}
            <div className="border-t pt-3">
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    data-testid="button-danger-zone-toggle"
                  >
                    <div className="flex items-center text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Danger Zone
                    </div>
                    <ChevronDown className="h-3 w-3 transition-transform duration-200 [&[data-state=open]]:rotate-180" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-2">
                  <p className="text-xs text-muted-foreground px-1">
                    Deleting this deal is permanent and cannot be undone.
                  </p>
                  <Button
                    size="sm"
                    className="w-full justify-start text-xs"
                    variant="outline"
                    onClick={() => setIsDeleteDialogOpen(true)}
                    data-testid="button-delete-deal"
                  >
                    <Trash2 className="h-3 w-3 mr-1 text-destructive" />
                    <span className="text-destructive">Delete Deal</span>
                  </Button>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        }
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent data-testid="dialog-delete-confirmation">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deal.dealName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete" disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="button-confirm-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Deal"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* SPRINT-3 AC005: Revert Confirmation Dialog */}
      <AlertDialog open={isRevertDialogOpen} onOpenChange={setIsRevertDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will restore all archived facilities and revert the deal back to scenario stage. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revertMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                revertMutation.mutate();
                setIsRevertDialogOpen(false);
              }}
              disabled={revertMutation.isPending}
            >
              {revertMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  Reverting...
                </>
              ) : (
                "Revert to Scenario"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* F007: Stage Transition Modal */}
      <StageTransitionModal
        dealId={params?.id || ""}
        transition={selectedTransition}
        open={isTransitionModalOpen}
        onOpenChange={setIsTransitionModalOpen}
      />
    </>
  );
}
